import Component from "@glimmer/component";
import { tracked } from "@glimmer/tracking";
import { concat } from "@ember/helper";
import { action } from "@ember/object";
import { service } from "@ember/service";
import Revision from "discourse/components/modal/history/revision";
import Revisions from "discourse/components/modal/history/revisions";
import TopicFooter from "discourse/components/modal/history/topic-footer";
import PermanentlyDeleteConfirmModal from "discourse/components/modal/permanently-delete-confirm";
import { iconHTML } from "discourse/lib/icon-library";
import { sanitizeAsync } from "discourse/lib/text";
import Category from "discourse/models/category";
import Post from "discourse/models/post";
import DConditionalLoadingSpinner from "discourse/ui-kit/d-conditional-loading-spinner";
import DModal from "discourse/ui-kit/d-modal";
import { categoryBadgeHTML } from "discourse/ui-kit/helpers/d-category-link";
import dDasherize from "discourse/ui-kit/helpers/d-dasherize";
import { i18n } from "discourse-i18n";

function customTagArray(val) {
  if (!val) {
    return [];
  }
  if (!Array.isArray(val)) {
    val = [val];
  }
  return val;
}

export default class History extends Component {
  @service dialog;
  @service modal;
  @service site;
  @service currentUser;
  @service siteSettings;

  @tracked loading;
  @tracked postRevision;
  @tracked viewMode = this.site.mobileView ? "inline" : "side_by_side";
  @tracked bodyDiff;
  @tracked initialLoad = true;
  @tracked error;

  constructor() {
    super(...arguments);
    this.refresh(this.args.model.postId, this.args.model.postVersion);
  }

  get loadFirstDisabled() {
    return (
      this.loading ||
      this.postRevision?.current_revision <= this.postRevision?.first_revision
    );
  }

  get loadPreviousDisabled() {
    return (
      this.loading ||
      !this.postRevision?.previous_revision ||
      (!this.postRevision?.previous_revision &&
        this.postRevision?.current_revision <=
          this.postRevision?.previous_revision)
    );
  }

  get loadNextDisabled() {
    return (
      this.loading ||
      this.postRevision?.current_revision >= this.postRevision?.next_revision
    );
  }

  get loadLastDisabled() {
    return (
      this.loading ||
      this.postRevision?.current_revision >= this.postRevision?.next_revision
    );
  }

  get displayRevisions() {
    return this.postRevision?.version_count > 2;
  }

  get modalTitleKey() {
    return this.args.model.post.version > 100
      ? "history_capped_revisions"
      : "history";
  }

  get previousVersion() {
    return this.postRevision?.current_version
      ? this.postRevision?.current_version - 1
      : null;
  }

  get revisionsText() {
    return i18n(
      "post.revisions.controls.comparing_previous_to_current_out_of_total",
      {
        previous: this.previousVersion,
        icon: iconHTML("left-right"),
        current: this.postRevision?.current_version,
        total: this.postRevision?.version_count,
      }
    );
  }

  get titleDiff() {
    let mode = this.viewMode;
    if (mode === "side_by_side_markdown") {
      mode = "side_by_side";
    }
    return this.postRevision?.title_changes?.[mode];
  }

  get bodyDiffHTML() {
    return this.postRevision?.body_changes?.[this.viewMode];
  }

  get diffHidden() {
    return (
      !this.postRevision?.body_changes &&
      !this.postRevision?.diff_error &&
      (this.postRevision?.previous_hidden || this.postRevision?.current_hidden)
    );
  }

  @action
  async calculateBodyDiff(_, [bodyDiff]) {
    let html = bodyDiff;
    if (this.viewMode !== "side_by_side_markdown") {
      const opts = {
        features: { editHistory: true, historyOneboxes: true },
        allowListed: {
          editHistory: { custom: (tag, attr) => attr === "class" },
          historyOneboxes: ["header", "article", "div[style]"],
        },
      };
      html = await sanitizeAsync(html, opts);
    }
    this.bodyDiff = html;
  }

  get previousTagChanges() {
    const previousArray = customTagArray(
      this.postRevision?.tags_changes?.previous
    );
    const currentSet = new Set(
      customTagArray(this.postRevision?.tags_changes?.current)
    );

    return previousArray.map((name) => ({
      name,
      deleted: !currentSet.has(name),
    }));
  }

  get currentTagChanges() {
    const previousSet = new Set(
      customTagArray(this.postRevision?.tags_changes?.previous)
    );
    const currentArray = customTagArray(
      this.postRevision?.tags_changes?.current
    );

    return currentArray.map((name) => ({
      name,
      inserted: !previousSet.has(name),
    }));
  }

  get createdAtDate() {
    return moment(this.postRevision?.created_at).format("LLLL");
  }

  get displayEdit() {
    return !!(
      this.postRevision?.can_edit &&
      this.args.model.editPost &&
      this.postRevision?.last_revision === this.postRevision?.current_revision
    );
  }

  get revertToRevisionText() {
    if (this.previousVersion) {
      return i18n("post.revisions.controls.revert", {
        revision: this.previousVersion,
      });
    }
  }

  async refresh(postId, postVersion) {
    this.loading = true;
    this.error = null;
    try {
      const result = await Post.loadRevision(postId, postVersion);
      this.postRevision = result;
      if (result.diff_error) {
        this.error = i18n("post.revisions.diff_too_complex");
      }
    } catch (error) {
      this.error =
        error.jqXHR?.responseJSON?.errors?.[0] || i18n("generic_error");

      const postStream = this.args.model.post?.topic?.postStream;
      if (postStream) {
        postStream.triggerChangedPost(postId, this.args.model);
      }
    } finally {
      this.loading = false;
      this.initialLoad = false;
    }
  }

  hide(postId, postVersion) {
    Post.hideRevision(postId, postVersion).then(() =>
      this.refresh(postId, postVersion)
    );
  }

  show(postId, postVersion) {
    Post.showRevision(postId, postVersion).then(() =>
      this.refresh(postId, postVersion)
    );
  }

  async revert(post, postVersion) {
    try {
      const result = await post.revertToRevision(postVersion);
      this.refresh(post.id, postVersion);
      if (result.topic) {
        post.set("topic.slug", result.topic.slug);
        post.set("topic.title", result.topic.title);
        post.set("topic.fancy_title", result.topic.fancy_title);
      }
      if (result.category_id) {
        post.set(
          "topic.category",
          await Category.asyncFindById(result.category_id)
        );
      }
      if (result.post) {
        // `PostSerializer` omits `reply_to_user` when the user is nil OR
        // when `suppress_reply_when_quoting` is on and the post quotes its
        // target. Treat a missing key as "leave alone" — only clear it
        // when the post number is also cleared.
        const props = {
          reply_to_post_number: result.post.reply_to_post_number ?? null,
        };
        if ("reply_to_user" in result.post) {
          props.reply_to_user = result.post.reply_to_user;
        } else if (result.post.reply_to_post_number == null) {
          props.reply_to_user = null;
        }
        post.setProperties(props);
      }
      this.args.closeModal();
    } catch (e) {
      if (e.jqXHR.responseJSON?.errors?.[0]) {
        this.dialog.alert(e.jqXHR.responseJSON.errors[0]);
      }
    }
  }

  get editButtonLabel() {
    return `post.revisions.controls.${
      this.postRevision?.wiki ? "edit_wiki" : "edit_post"
    }`;
  }

  get hiddenClasses() {
    if (this.diffHidden) {
      return null;
    }

    if (this.viewMode === "inline") {
      return this.postRevision?.previous_hidden ||
        this.postRevision?.current_hidden
        ? "hidden-revision-either"
        : null;
    } else {
      let result = [];
      if (this.postRevision?.previous_hidden) {
        result.push("hidden-revision-previous");
      }
      if (this.postRevision?.current_hidden) {
        result.push("hidden-revision-current");
      }
      return result.join(" ");
    }
  }

  get previousCategory() {
    if (this.postRevision?.category_id_changes?.previous) {
      let category = Category.findById(
        this.postRevision?.category_id_changes.previous
      );
      return categoryBadgeHTML(category, {
        allowUncategorized: true,
        extraClasses: "diff-del",
      });
    }
  }

  get currentCategory() {
    if (this.postRevision?.category_id_changes?.current) {
      let category = Category.findById(
        this.postRevision?.category_id_changes.current
      );
      return categoryBadgeHTML(category, {
        allowUncategorized: true,
        extraClasses: "diff-ins",
      });
    }
  }

  @action
  displayInline(event) {
    event?.preventDefault();
    this.viewMode = "inline";
  }

  @action
  displaySideBySide(event) {
    event?.preventDefault();
    this.viewMode = "side_by_side";
  }

  @action
  displaySideBySideMarkdown(event) {
    event?.preventDefault();
    this.viewMode = "side_by_side_markdown";
  }

  @action
  loadFirstVersion() {
    this.refresh(this.postRevision?.post_id, this.postRevision?.first_revision);
  }

  @action
  loadPreviousVersion() {
    this.refresh(
      this.postRevision?.post_id,
      this.postRevision?.previous_revision
    );
  }

  @action
  loadNextVersion() {
    this.refresh(this.postRevision?.post_id, this.postRevision?.next_revision);
  }

  @action
  loadLastVersion() {
    return this.refresh(
      this.postRevision?.post_id,
      this.postRevision?.last_revision
    );
  }

  @action
  hideVersion() {
    this.hide(this.postRevision?.post_id, this.postRevision?.current_revision);
  }

  @action
  permanentlyDeleteVersions() {
    const postId = this.postRevision?.post_id;
    this.args.closeModal();

    this.modal.show(PermanentlyDeleteConfirmModal, {
      model: {
        message: i18n("post.revisions.controls.destroy_confirm"),
        confirmPhrase: i18n("post.controls.permanently_delete_confirm_phrase"),
        didConfirm: () => {
          Post.permanentlyDeleteRevisions(postId);
        },
      },
    });
  }

  @action
  showVersion() {
    this.show(this.postRevision?.post_id, this.postRevision?.current_revision);
  }

  @action
  editPost() {
    this.args.model.editPost(this.args.model.post);
    this.args.closeModal();
  }

  @action
  revertToVersion() {
    this.revert(this.args.model.post, this.postRevision?.current_revision);
  }

  <template>
    <DModal
      @title={{i18n this.modalTitleKey}}
      @closeModal={{@closeModal}}
      class="history-modal --max
        {{concat '--mode-' (dDasherize this.viewMode)}}"
    >
      <:body>
        {{#if this.error}}
          <div class="alert alert-error">{{this.error}}</div>
        {{/if}}
        <DConditionalLoadingSpinner @condition={{this.initialLoad}}>
          {{#if this.postRevision}}
            <Revision
              @model={{this.postRevision}}
              @previousCategory={{this.previousCategory}}
              @currentCategory={{this.currentCategory}}
              @displayInline={{this.displayInline}}
              @displaySideBySide={{this.displaySideBySide}}
              @displaySideBySideMarkdown={{this.displaySideBySideMarkdown}}
              @viewMode={{this.viewMode}}
            />
            <Revisions
              @model={{this.postRevision}}
              @hiddenClasses={{this.hiddenClasses}}
              @diffHidden={{this.diffHidden}}
              @mobileView={{this.site.mobileView}}
              @userChanges={{this.user_changes}}
              @previousCategory={{this.previousCategory}}
              @currentCategory={{this.currentCategory}}
              @previousTagChanges={{this.previousTagChanges}}
              @currentTagChanges={{this.currentTagChanges}}
              @bodyDiffHTML={{this.bodyDiffHTML}}
              @bodyDiff={{this.bodyDiff}}
              @calculateBodyDiff={{this.calculateBodyDiff}}
              @titleDiff={{this.titleDiff}}
              @viewMode={{this.viewMode}}
            />
          {{/if}}
        </DConditionalLoadingSpinner>
      </:body>
      <:footer>
        {{#if @model.editPost}}
          {{#if this.postRevision}}
            <TopicFooter
              @model={{this.postRevision}}
              @loadFirstVersion={{this.loadFirstVersion}}
              @loadPreviousVersion={{this.loadPreviousVersion}}
              @loadNextVersion={{this.loadNextVersion}}
              @loadLastVersion={{this.loadLastVersion}}
              @displayEdit={{this.displayEdit}}
              @editPost={{this.editPost}}
              @editButtonLabel={{this.editButtonLabel}}
              @revertToVersion={{this.revertToVersion}}
              @hideVersion={{this.hideVersion}}
              @showVersion={{this.showVersion}}
              @permanentlyDeleteVersions={{this.permanentlyDeleteVersions}}
              @loading={{this.loading}}
              @canPermanentlyDelete={{this.siteSettings.can_permanently_delete}}
              @loadFirstDisabled={{this.loadFirstDisabled}}
              @loadPreviousDisabled={{this.loadPreviousDisabled}}
              @displayRevisions={{this.displayRevisions}}
              @revisionsText={{this.revisionsText}}
              @loadNextDisabled={{this.loadNextDisabled}}
              @loadLastDisabled={{this.loadLastDisabled}}
              @revertToRevisionText={{this.revertToRevisionText}}
              @isStaff={{this.currentUser.staff}}
            />
          {{/if}}
        {{/if}}
      </:footer>
    </DModal>
  </template>
}
